import React, { useMemo } from 'react';
import {
  planWaveformWindow,
  reduceWaveformWindow,
} from '@src/services/waveformWindow';
import Waveform from './index';
import { StyledWindow } from './Virtualized.style';

/**
 * <Waveform> for a peaks array too dense to draw whole — the BE source
 * waveform is ~50 peaks/second, so a long video is hundreds of thousands of
 * points and one SVG path over them all froze the timeline. This draws ONLY
 * the visible window, reduced to its max envelope at about one point per
 * pixel — the classic waveform shape, and the same reduction the old capped
 * decode showed. When the zoom is fine enough that a peak spans a pixel or
 * more, the raw peaks are drawn exactly — byte-for-byte the BE shape.
 *
 * A wrapper, not a change: <Waveform> itself is untouched, so the voice/music
 * blocks that draw their few hundred peaks whole keep exactly their behaviour.
 *
 * Coordinates are the clip's own: `fromSec`/`toSec` are the visible range,
 * and the window is absolutely positioned at second×pxPerSec inside the
 * (position: relative) band this mounts into.
 */
const VirtualizedWaveform = ({
  peaks,
  durationSec,
  fromSec,
  toSec,
  pxPerSec,
  color,
}) => {
  const plan = planWaveformWindow({
    peakCount: peaks.length,
    durationSec,
    fromSec,
    toSec,
    pxPerSec,
  });
  // Primitive deps, not `plan`: its identity changes every render, but the
  // bucket-aligned window it describes only changes when the view leaves the
  // planned slack or the zoom changes — scrolling inside it reuses the
  // envelope.
  const { bucketSize = 0, firstBucket = 0, bucketCount = 0 } = plan || {};
  const envelope = useMemo(
    () =>
      bucketCount
        ? reduceWaveformWindow(peaks, { firstBucket, bucketCount, bucketSize })
        : null,
    [peaks, firstBucket, bucketCount, bucketSize],
  );

  if (!plan || !envelope) return null;

  return (
    <StyledWindow style={{ left: plan.leftPx, width: plan.widthPx }}>
      <Waveform peaks={envelope} color={color} />
    </StyledWindow>
  );
};

export default VirtualizedWaveform;
